import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import * as Icon from 'react-feather';
import {
  capitalize,
  extractIds,
  getItemDataOrAdditionPrice,
  getLocalizedCategoryName,
  getLocalizedName,
  isAdminMode as _isAdminMode,
} from '../../../../utility/util/util.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import {
  setCreateAdditionModalData,
  setCreateItemAdditionGroupModalData,
  setEditAdditionModalData,
  setEditItemAdditionGroupModalData,
  setIsCreateAdditionModalOpen,
  setIsCreateItemAdditionGroupModalOpen,
  setIsEditAdditionModalOpen,
  setIsEditItemAdditionGroupModalOpen,
  setIsListAdditionsModalOpen,
} from '../../../../redux/modal/modal.actions';
import RemoveConfirmationModal from '../remove-confirmation/remove-confirmation.modal';
import {
  removeAddition,
  removeAdditionGroup,
  removeAdditionGroupEntry,
} from './list-additions.service';
import {
  fetchAdditionGroups,
  fetchFoodItemAdditions,
  fetchItemDatas,
  fetchMenuEntries,
  fetchMenuItems,
} from '../../../../redux/store/store.actions';
import Empty from '../../../../views/errors/empty.component';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import {
  basePatchQuery,
  basePostQuery,
} from '../../../../utility/sb-api/sb-api.service';
import { Menu } from 'react-feather';
import { ORDER_TYPE_INHOUSE } from '../../../../utility/constants';

const ListAdditionsModal = ({
  isOpen,
  data,
  foodItemAdditions,
  additionGroups,
  store,
  me,

  setIsListAdditionsModalOpen,
  setIsEditAdditionModalOpen,
  setEditAdditionModalData,
  fetchFoodItemAdditions,
  setIsCreateAdditionModalOpen,
  setCreateAdditionModalData,
  setIsEditItemAdditionGroupModalOpen,
  setEditItemAdditionGroupModalData,
  setCreateItemAdditionGroupModalData,
  setIsCreateItemAdditionGroupModalOpen,
  fetchAdditionGroups,
  fetchMenuEntries,
  fetchMenuItems,
  fetchItemDatas,
}) => {
  const intl = useIntl();
  const [sortedAdditionGroups, setSortedAdditionGroups] = useState(null);
  const [removeAdditionId, setRemoveAdditionId] = useState(null);
  const [removeAdditionGroupId, setRemoveAdditionGroupId] = useState(null);
  const [removeAdditionGroupEntryId, setRemoveAdditionGroupEntryId] = useState(
    null,
  );

  const referencedAdditionIds = new Set(
    additionGroups?.flatMap((group) =>
      group?.entries.map((entry) => entry.addition.id),
    ),
  );
  const additionsWithoutGroup = foodItemAdditions?.filter(
    (addition) => !referencedAdditionIds.has(addition.id),
  );
  const isAdminMode = _isAdminMode(me, store?.slug);

  useEffect(() => {
    if (additionGroups) {
      additionGroups
        .sort((a, b) => a.manualRanking - b.manualRanking)
        .forEach((additionGroup) =>
          additionGroup.entries.sort(
            (a, b) => a.manualRanking - b.manualRanking,
          ),
        );
      setSortedAdditionGroups(additionGroups); // sort by creation date
    }
  }, [additionGroups]);

  const closeModal = () => setIsListAdditionsModalOpen(false);

  const onRemoveClick = (obj) => {
    const { additionId, additionGroupId, additionGroupEntryId } = obj;
    closeModal();
    if (additionId) {
      setRemoveAdditionId(additionId);
    } else if (additionGroupEntryId) {
      setRemoveAdditionGroupEntryId(additionGroupEntryId);
    } else {
      setRemoveAdditionGroupId(additionGroupId);
    }
  };

  const onEditClick = (entry, additionGroup) => {
    setEditAdditionModalData({
      additionId: entry.addition.id,
      groupEntry: entry,
      additionGroup,
      onSuccess: () => setIsListAdditionsModalOpen(true),
    });
    setIsEditAdditionModalOpen(true);
  };

  const SortableAdditionGroupEntryContainer = SortableContainer(
    ({ additionGroup }) => (
      <div>
        {additionGroup?.entries.map((entry, index) => (
          <SortableAdditionGroupEntry
            index={index}
            additionGroup={additionGroup}
            entry={entry}
            key={index}
          />
        ))}
      </div>
    ),
  );

  const SortableAdditionGroupEntry = SortableElement(
    ({ additionGroup, entry, index }) => (
      <div
        key={index}
        className="d-flex align-items-center justify-content-between mb-1"
      >
        <span className="text-nowrap text-truncate">
          <SortableAdditionGroupEntryDragHandle />
          {getLocalizedName(entry.addition, intl?.locale)}
        </span>
        <div className="d-flex align-items-center">
          <span className="d-flex align-items-center mr-2 ml-2">
            {getItemDataOrAdditionPrice(ORDER_TYPE_INHOUSE, entry.addition) ===
            0
              ? intl.formatMessage({ id: 'priceFree' }).toUpperCase()
              : intl.formatNumber(
                  getItemDataOrAdditionPrice(
                    ORDER_TYPE_INHOUSE,
                    entry.addition,
                  ),
                  { format: 'priceCurrency' },
                )}
          </span>
          <span onClick={() => onEditClick(entry, additionGroup)}>
            <Icon.Edit size={22} className="secondary mr-2" />
          </span>
          <span
            onClick={() => onRemoveClick({ additionId: entry.addition.id })}
          >
            <Icon.Trash2 size={22} className="danger" />
          </span>
        </div>
      </div>
    ),
  );

  const SortableAdditionGroupEntryDragHandle = SortableHandle(() => (
    <span className="pr-1">
      <Menu />
    </span>
  ));

  const onAdditionGroupEntrySortEnd = (additionGroup, sortEnd) => {
    const resortedGroupEntries = arrayMove(
      additionGroup.entries,
      sortEnd.oldIndex,
      sortEnd.newIndex,
    );
    const updatedAdditionGroup = {
      ...additionGroup,
      entries: resortedGroupEntries,
    };

    setSortedAdditionGroups((prevState) =>
      prevState.map((currentAdditionGroup) =>
        currentAdditionGroup.id !== additionGroup.id
          ? currentAdditionGroup
          : updatedAdditionGroup,
      ),
    );

    const basePath = store?.id
      ? `manager/stores/${store.id}/item-addition-groups`
      : 'item-addition-groups';
    basePostQuery(
      `${basePath}/${additionGroup.id}/manual-ranking`,
      { manualRankings: resortedGroupEntries.map(({ id }) => id) },
      null,
      'changesSaved',
      'changesSavedError',
    );
  };

  const SortableAdditionGroupContainer = SortableContainer(
    ({ additionGroups }) => (
      <div>
        {additionGroups?.map((additionGroup, index) => (
          <SortableAdditionGroup
            additionGroup={additionGroup}
            key={index}
            index={index}
          />
        ))}
      </div>
    ),
  );

  const SortableAdditionGroup = SortableElement(({ additionGroup, index }) => (
    <div key={index}>
      <span className="d-flex align-items-center justify-content-between">
        <span
          className="text-nowrap text-truncate font-medium-1 font-weight-bold primary"
          data-testid="testGroupName"
        >
          <SortableAdditionGroupDragHandle />
          {getLocalizedName(additionGroup, intl.locale)}
        </span>
        <div className="d-flex align-items-center">
          <span
            onClick={() => {
              setEditItemAdditionGroupModalData({
                itemAdditionGroup: additionGroup,
              });
              setIsEditItemAdditionGroupModalOpen(true);
            }}
          >
            <Icon.Edit size={22} className="secondary mr-2" />
          </span>
          <span
            onClick={() =>
              onRemoveClick({ additionGroupId: additionGroup?.id })
            }
          >
            <Icon.Trash2
              size={22}
              className="danger"
              data-testid="removeAdditionButton"
            />
          </span>
        </div>
      </span>
      {!additionGroup ? null : <hr className="my-50" />}
      <div className="align-items-center justify-content-between" key={index}>
        <SortableAdditionGroupEntryContainer
          additionGroup={additionGroup}
          axis="y"
          onSortEnd={(sortEnd) =>
            onAdditionGroupEntrySortEnd(additionGroup, sortEnd)
          }
          helperClass="sortable-on-drag"
          useDragHandle={true}
        />
      </div>
    </div>
  ));

  const SortableAdditionGroupDragHandle = SortableHandle(() => (
    <span className="pr-1">
      <Menu />
    </span>
  ));

  const onAdditionGroupSortEnd = (additionGroups, sortEnd) => {
    const resortedGroups = arrayMove(
      additionGroups,
      sortEnd.oldIndex,
      sortEnd.newIndex,
    );
    setSortedAdditionGroups(resortedGroups);

    const basePath = store?.id
      ? `manager/stores/${store.id}/item-addition-groups`
      : 'item-addition-groups';
    basePostQuery(
      `${basePath}/manual-ranking`,
      { manualRankings: resortedGroups.map(({ id }) => id) },
      null,
      'changesSaved',
      'changesSavedError',
    );
  };

  return (
    <>
      <ModalSheet
        disableContentDrag={true}
        isOpen={isOpen}
        closeModal={closeModal}
        title={intl.formatMessage({ id: 'additionsTitle' })}
        footer={
          <div className="align-content-center">
            <Row>
              <Col xs={6} className="pr-50">
                <Button
                  data-testid="createExtrasGroup"
                  color="primary"
                  className="w-100 mt-1"
                  onClick={() => {
                    setCreateItemAdditionGroupModalData({
                      itemAdditionGroup: {
                        minSelections: 1,
                        freeOfChargeSelections: 0,
                      },
                    });
                    setIsCreateItemAdditionGroupModalOpen(true);
                  }}
                >
                  <FormattedMessage id="createNewAdditionGroupTitle" />
                </Button>
              </Col>
              <Col xs={6} className="pl-50">
                <Button
                  data-testid="createAddition"
                  color="primary"
                  className="w-100 mt-1"
                  onClick={() => {
                    setCreateAdditionModalData({
                      onSuccess: () => setIsListAdditionsModalOpen(true),
                    });
                    setIsCreateAdditionModalOpen(true);
                  }}
                >
                  <FormattedMessage id="createNewAdditionTitle" />
                </Button>
              </Col>
            </Row>
          </div>
        }
      >
        <div className="px-1">
          {sortedAdditionGroups?.length > 0 ||
          additionsWithoutGroup?.length > 0 ? (
            <>
              <SortableAdditionGroupContainer
                data-testid="additions"
                additionGroups={sortedAdditionGroups}
                axis="y"
                onSortEnd={(sortEnd) =>
                  onAdditionGroupSortEnd(sortedAdditionGroups, sortEnd)
                }
                helperClass="sortable-on-drag"
                useDragHandle={true}
              />

              {additionsWithoutGroup?.length > 0 && (
                <div>
                  <span className="d-flex align-items-center justify-content-between">
                    <span
                      className="text-nowrap text-truncate font-medium-1 font-weight-bold primary"
                      data-testid="unsignedLabel"
                    >
                      <FormattedMessage id="unassignedAdditionsLabel" />
                    </span>
                  </span>
                  <hr className="my-50" />
                  <div className="align-items-center justify-content-between">
                    {additionsWithoutGroup?.map((addition) => (
                      <>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span
                            className="text-nowrap text-truncate"
                            data-testid="addition"
                          >
                            {getLocalizedName(addition, intl?.locale)}
                          </span>
                          <div className="d-flex align-items-center">
                            <span className="d-flex align-items-center mr-2 ml-2">
                              {getItemDataOrAdditionPrice(
                                ORDER_TYPE_INHOUSE,
                                addition,
                              ) === 0
                                ? intl
                                    .formatMessage({ id: 'priceFree' })
                                    .toUpperCase()
                                : intl.formatNumber(
                                    getItemDataOrAdditionPrice(
                                      ORDER_TYPE_INHOUSE,
                                      addition,
                                    ),
                                    { format: 'priceCurrency' },
                                  )}
                            </span>
                            <span
                              onClick={() => {
                                setEditAdditionModalData({
                                  additionId: addition.id,
                                  groupEntry: null,
                                  onSuccess: () =>
                                    setIsListAdditionsModalOpen(true),
                                });
                                setIsEditAdditionModalOpen(true);
                              }}
                            >
                              <Icon.Edit
                                size={22}
                                className="secondary mr-2"
                                data-testid="editAdditionButton"
                              />
                            </span>
                            <span
                              onClick={() =>
                                onRemoveClick({ additionId: addition.id })
                              }
                            >
                              <Icon.Trash2
                                size={22}
                                className="danger"
                                data-testid="removeAdditionButton"
                              />
                            </span>
                          </div>
                        </div>
                      </>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Empty className="my-2" titleId="emptyAdditionsListTitle" />
          )}
        </div>
      </ModalSheet>
      <RemoveConfirmationModal
        isOpen={
          !!removeAdditionId ||
          !!removeAdditionGroupId ||
          !!removeAdditionGroupEntryId
        }
        closeModal={() => {
          setRemoveAdditionId(null);
          setRemoveAdditionGroupId(null);
          setRemoveAdditionGroupEntryId(null);
        }}
        titleId="confirm"
        messageId={
          !!removeAdditionGroupId
            ? 'confirmDeleteAdditionGroup'
            : 'removeAdditionConfirmation'
        }
        handleRemoval={() => {
          const storeId = store?.id;
          if (removeAdditionId) {
            removeAddition(removeAdditionId, storeId, async () => {
              const menuId = store?.menu?.id;
              await Promise.all([
                fetchFoodItemAdditions(isAdminMode, menuId, undefined, storeId),
                fetchMenuItems(isAdminMode, menuId, undefined, storeId),
                fetchItemDatas(isAdminMode, menuId, undefined, storeId),
              ]);
              await fetchAdditionGroups(
                isAdminMode,
                menuId,
                undefined,
                storeId,
              );
              await fetchMenuEntries(
                menuId,
                isAdminMode,
                () => setIsListAdditionsModalOpen(true),
                storeId,
              );
            });
          } else if (removeAdditionGroupEntryId) {
            removeAdditionGroupEntry(
              removeAdditionGroupEntryId,
              storeId,
              async () => {
                const menuId = store?.menu?.id;
                await Promise.all([
                  fetchFoodItemAdditions(
                    isAdminMode,
                    menuId,
                    undefined,
                    storeId,
                  ),
                  fetchMenuItems(isAdminMode, menuId, undefined, storeId),
                  fetchItemDatas(isAdminMode, menuId, undefined, storeId),
                ]);
                await fetchAdditionGroups(
                  isAdminMode,
                  menuId,
                  undefined,
                  storeId,
                );
                await fetchMenuEntries(
                  menuId,
                  isAdminMode,
                  () => setIsListAdditionsModalOpen(true),
                  storeId,
                );
              },
            );
          } else {
            removeAdditionGroup(removeAdditionGroupId, storeId, async () => {
              const menuId = store?.menu?.id;
              await Promise.all([
                fetchFoodItemAdditions(isAdminMode, menuId, undefined, storeId),
                fetchMenuItems(isAdminMode, menuId, undefined, storeId),
                fetchItemDatas(isAdminMode, menuId, undefined, storeId),
              ]);
              await fetchAdditionGroups(
                isAdminMode,
                menuId,
                undefined,
                storeId,
              );
              await fetchMenuEntries(
                menuId,
                isAdminMode,
                () => setIsListAdditionsModalOpen(true),
                storeId,
              );
            });
          }
        }}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isListAdditionsModalOpen,
  data: state.modals.listAdditionsModalData,
  foodItemAdditions: state.store.foodItemAdditions,
  additionGroups: state.store.additionGroups,
  store: state.store.store,
  me: state.me.me,
});

export default connect(mapStateToProps, {
  setIsListAdditionsModalOpen,
  setIsEditAdditionModalOpen,
  setEditAdditionModalData,
  fetchFoodItemAdditions,
  setIsCreateAdditionModalOpen,
  setCreateAdditionModalData,
  setIsEditItemAdditionGroupModalOpen,
  setEditItemAdditionGroupModalData,
  setCreateItemAdditionGroupModalData,
  setIsCreateItemAdditionGroupModalOpen,
  fetchAdditionGroups,
  fetchMenuEntries,
  fetchMenuItems,
  fetchItemDatas,
})(ListAdditionsModal);
